import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { PALETTE as P } from "../tokens";
import { useAuth } from "../context/AuthContext";
import { journalEntriesApi } from "../api/journalEntries";
import { useBackOrFallback } from "../hooks/useBackOrFallback";

// /journal/new — the free-write surface.
//
// Deliberately the emptiest screen in the app: a heading, a box, a save.
// There is no slider, no prompt sequence, no "how did that feel"
// follow-up. The check-in already owns structured daily input; this is
// the place with nothing asked of you.
//
// NOT SCORED. A free-write entry's extraction binds on
// journal_entry_id, which cannot satisfy the RCI join (see
// app/models/journal_entry.py). Nothing here should imply the text
// moves a number, because it doesn't — that's the feature.
//
// Input styling is CheckIn.jsx's brain-dump convention verbatim (the
// same textarea rule block), so the two writing surfaces feel like one
// affordance rather than two designs.

const STYLES = `
  @keyframes journalFadeUp {
    from { opacity: 0; transform: translateY(12px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  .je-fu { animation: journalFadeUp 0.5s ease forwards; }
  .je-d1 { animation-delay: 0.05s; opacity: 0; }
  .je-d2 { animation-delay: 0.18s; opacity: 0; }

  /* Lifted from CheckIn.jsx's brain-dump step — same border, radius,
     line-height and focus treatment, so writing feels identical on
     both surfaces. Scoped to .je-screen rather than a bare element
     selector so it can't leak into other screens' textareas. */
  .je-screen textarea {
    width: 100%; background: ${P.surface}; border: 1.5px solid ${P.border};
    border-radius: 16px; padding: 18px; font-family: 'DM Sans', sans-serif;
    font-size: 14px; font-weight: 300; color: ${P.text}; line-height: 1.75;
    resize: none; outline: none; transition: border-color 0.2s;
    box-sizing: border-box;
  }
  .je-screen textarea:focus { border-color: ${P.primaryMid}; }
  .je-screen textarea::placeholder { color: ${P.textFaint}; font-style: italic; }

  .je-save {
    width: 100%; padding: 17px; background: ${P.dark};
    border: none; border-radius: 16px; cursor: pointer;
    font-family: 'Playfair Display', Georgia, serif;
    font-size: 18px; font-style: italic; color: ${P.bg};
    transition: opacity 0.15s; letter-spacing: 0.01em;
  }
  .je-save:hover:not(:disabled) { opacity: 0.85; }
  .je-save:disabled { opacity: 0.35; cursor: default; }
`;

// Matches JournalEntryCreate.body's max_length on the server. Enforced
// here too so a long writer hits a visible ceiling rather than a 422.
const MAX_CHARS = 8000;

function todayKey(d = new Date()) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export default function JournalEntry() {
  const navigate = useNavigate();
  // Deep-link safe: a user can land here from the nav, from Home, or
  // from a pasted URL, and the chevron has to go somewhere either way.
  const goBack = useBackOrFallback("/journal");
  const { token } = useAuth();

  const [body, setBody] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const trimmed = body.trim();
  const canSave = trimmed.length > 0 && !saving;

  async function save() {
    if (!canSave) return;
    setSaving(true);
    setError(null);
    try {
      // entry_date is the user's LOCAL day. Letting the server default
      // it would file a 11pm entry under tomorrow for anyone east of
      // UTC, which is the same drift SignalHistory's _parseLocalDate
      // exists to avoid.
      await journalEntriesApi.create(trimmed, todayKey(), token);
      // replace: the compose screen shouldn't sit in history behind the
      // list, where Back would reopen a box the user just emptied.
      navigate("/journal", { replace: true });
    } catch {
      // Reflect/Contextualize register, same as SharedEntriesModal's
      // error copy: say what happened and what is still true. "Try
      // again" is directive and the patient rubric rejects it — the
      // Save button is right there and is allowed to be imperative.
      setError("That didn't save. Your writing is still here — nothing was lost.");
      setSaving(false);
    }
  }

  return (
    <>
      <style>{STYLES}</style>
      <div
        className="je-screen"
        style={{
          minHeight: "100vh",
          background: P.bg,
          padding:
            "max(20px, calc(env(safe-area-inset-top) + 12px)) 24px var(--safe-bottom)",
          boxSizing: "border-box",
        }}
      >
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 28 }}>
          <button
            type="button"
            onClick={goBack}
            aria-label="Back"
            style={{
              all: "unset", cursor: "pointer",
              fontSize: 20, color: P.primary, minWidth: 28,
            }}
          >
            ←
          </button>
          <span style={{
            fontSize: 11, fontWeight: 500, color: P.primary,
            letterSpacing: "0.08em", textTransform: "uppercase",
          }}>
            New entry
          </span>
        </div>

        <div className="je-fu je-d1" style={{ marginBottom: 24 }}>
          <h1 style={{
            fontFamily: "'Playfair Display', Georgia, serif",
            fontSize: 30, fontStyle: "italic", fontWeight: 400,
            color: P.text, lineHeight: 1.2, margin: "0 0 12px",
          }}>
            Whatever&rsquo;s here.
          </h1>
          {/* Reflect/Contextualize register — describes the surface,
              doesn't prescribe what to do with it. */}
          <p style={{
            fontSize: 13, color: P.textMid, fontWeight: 300,
            lineHeight: 1.65, margin: 0,
          }}>
            This is a page with nothing asked of it. Nothing written here
            counts toward your signal.
          </p>
          {/* Scoring is the only thing this line addresses. AI
              transmission is disclosed in full on Profile ("Sends your
              reflections, scores, and wearable-derived labels to
              Anthropic (Claude)…"), next to the switch itself — saying
              it again here would turn the compose screen into a policy
              page at the moment someone sat down to write. */}
          <p style={{
            fontSize: 12, color: P.textMute, fontWeight: 300,
            lineHeight: 1.6, margin: "8px 0 0",
          }}>
            What&rsquo;s written here isn&rsquo;t scored.
          </p>
        </div>

        <div className="je-fu je-d2">
          <textarea
            rows={12}
            maxLength={MAX_CHARS}
            placeholder="Write whatever comes — a feeling, a thought, a sentence fragment. This is yours."
            value={body}
            onChange={(e) => setBody(e.target.value)}
            aria-label="Journal entry"
            style={{ marginBottom: 10 }}
          />

          <div style={{
            display: "flex", justifyContent: "flex-end",
            minHeight: 18, marginBottom: 20,
          }}>
            {body.length > 0 && (
              <span style={{ fontSize: 11, color: P.textMute }}>
                {body.length} characters
              </span>
            )}
          </div>

          {error && (
            <p role="alert" style={{
              fontSize: 13, color: P.text, background: P.surfaceAlt,
              borderRadius: 12, padding: "10px 12px", margin: "0 0 16px",
            }}>
              {error}
            </p>
          )}

          <button
            type="button"
            className="je-save"
            onClick={save}
            disabled={!canSave}
          >
            {saving ? "Saving…" : "Save entry"}
          </button>
        </div>
      </div>
    </>
  );
}
